import React from 'react';
import { Link } from 'react-router-dom';
import { Cog6ToothIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { Navigation } from './Navigation';
import { NavigationItem } from '../../../navigation/types';

interface SidebarProps {
  navigation: NavigationItem[];
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ navigation, collapsed, onToggle }) => {
  const buildNumber: string = (import.meta.env.VITE_BUILD_NUMBER as string) || '';

  return (
    <Transition
      as="div"
      show={true}
      className={`hidden lg:flex lg:inset-y-0 lg:z-50 lg:flex-col transition-all duration-200 ${collapsed ? 'lg:w-20' : 'lg:w-72'}`}
    >
      <div className={`flex grow flex-col gap-y-5 overflow-y-auto overflow-x-hidden border-r border-gray-200 bg-white ${collapsed ? 'px-2' : 'px-6'} pb-4 transition-all duration-200`}>
        <div className={`flex h-16 shrink-0 items-center justify-between ${collapsed ? 'px-0' : ''}`}>
          <Link to="/">
            <div className="flex items-center">
              <img
                alt="Paykonnect"
                src="/pk.png"
                className="h-8 w-auto mx-auto"
              />
              {!collapsed && (
                <span className="text-gray-700 hover:text-indigo-600 p-2 text-xl/6 font-semibold transition-opacity duration-200">
                  {import.meta.env.VITE_APP_NAME}
                </span>
              )}
            </div>
          </Link>
          <button
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={onToggle}
            className="rounded p-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>
        <nav className="flex flex-1 flex-col">
          <Navigation items={navigation} collapsed={collapsed} />
          <div className="mt-auto">
            <Link
              to="/settings"
              className={`group flex ${collapsed ? 'justify-center' : 'gap-x-3'} rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600`}
            >
              <Cog6ToothIcon
                aria-hidden={true}
                className="text-gray-400 group-hover:text-indigo-600 size-6 shrink-0"
              />
              {!collapsed && 'Settings'}
            </Link>
            {buildNumber && !collapsed && (
              <div className="text-xs text-gray-400 mt-2" data-testid="build-number">
                Build: {buildNumber}
              </div>
            )}
          </div>
        </nav>
      </div>
    </Transition>
  );
};

export default Sidebar;

// Example AppShell layout for full responsiveness
//
// <div className="flex h-screen">
//   <Sidebar navigation={navigation} collapsed={collapsed} onToggle={toggleSidebar} />
//   <main className="flex-1 min-w-0 bg-gray-50">
//     {/* Main content here */}
//   </main>
// </div>